// El reporte de compras de leche que pide el contador para la DIAN.
//
// ── Qué tiene que llevar y por qué ──
//
// A quién se le compró, con CÉDULA. Sin cédula el renglón no sirve para
// declarar: la DIAN no identifica a un proveedor por el nombre de la finca.
// Por eso este reporte no solo lista — también dice a quién le falta el
// documento, que es lo único que de verdad bloquea la presentación.
//
// Y separado por leche, porque son dos productos con precios distintos y el
// contador los imputa aparte.

const num = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

const soloFecha = (f) => String(f || '').slice(0, 10);

// A quién le falta la cédula. Es lo primero que hay que ver: un reporte
// perfecto con tres proveedores sin identificar no se puede presentar.
export function sinCedula(filas) {
  const vistos = new Map();
  for (const f of filas || []) {
    const doc = String(f.documento || '').trim();
    if (doc) continue;
    if (!vistos.has(f.finca_id)) {
      vistos.set(f.finca_id, {
        finca_id: f.finca_id, finca: f.finca,
        propietario: f.propietario || null, total: 0,
      });
    }
    vistos.get(f.finca_id).total += num(f.total);
  }
  // De mayor a menor: si hay que llamar a alguien a pedirle la cédula, se
  // empieza por el que más plata mueve.
  return [...vistos.values()].sort((a, b) => b.total - a.total);
}

// Una fila por finca, con sus días adentro. Es como lo lee el contador: no le
// sirve un listado plano de 600 renglones sin agrupar.
export function porFinca(filas) {
  const mapa = new Map();
  for (const f of filas || []) {
    if (!mapa.has(f.finca_id)) {
      mapa.set(f.finca_id, {
        finca_id: f.finca_id, finca: f.finca,
        propietario: f.propietario || null,
        documento: String(f.documento || '').trim() || null,
        ubicacion: f.ubicacion || null,
        dias: [], litrosVaca: 0, valorVaca: 0, litrosBufala: 0, valorBufala: 0,
        litros: 0, total: 0,
      });
    }
    const a = mapa.get(f.finca_id);
    const esBufala = f.tipo_leche === 'bufala';
    const litros = num(f.litros), total = num(f.total);
    a.dias.push({
      fecha: soloFecha(f.fecha),
      litrosVaca: esBufala ? 0 : litros, valorVaca: esBufala ? 0 : total,
      litrosBufala: esBufala ? litros : 0, valorBufala: esBufala ? total : 0,
      precio: num(f.precio_litro),
    });
    if (esBufala) { a.litrosBufala += litros; a.valorBufala += total; }
    else { a.litrosVaca += litros; a.valorVaca += total; }
    a.litros += litros;
    a.total += total;
  }
  // Los días de cada finca en orden, y las fincas por nombre: así se busca a
  // alguien en un papel impreso.
  for (const a of mapa.values()) {
    a.dias.sort((x, y) => x.fecha.localeCompare(y.fecha));
  }
  return [...mapa.values()].sort((a, b) => String(a.finca).localeCompare(String(b.finca)));
}

export function totales(fincas) {
  return (fincas || []).reduce((t, f) => ({
    litrosVaca: t.litrosVaca + f.litrosVaca, valorVaca: t.valorVaca + f.valorVaca,
    litrosBufala: t.litrosBufala + f.litrosBufala, valorBufala: t.valorBufala + f.valorBufala,
    litros: t.litros + f.litros, total: t.total + f.total,
    proveedores: t.proveedores + 1,
  }), { litrosVaca: 0, valorVaca: 0, litrosBufala: 0, valorBufala: 0,
        litros: 0, total: 0, proveedores: 0 });
}

// ── El archivo para el contador ──────────────────────────────────────────

// Un campo de CSV. Las comillas se doblan y todo va entrecomillado: un nombre
// con coma —«Finca La Ceja, lote 2»— parte la fila en dos y el contador recibe
// un archivo corrido que no cuadra con nada.
const campo = (v) => '"' + String(v ?? '').replace(/"/g, '""') + '"';

// El CSV se separa con PUNTO Y COMA, no con coma.
//
// Excel en español lo abre en columnas con punto y coma; con coma mete todo en
// una sola columna y el contador tiene que repartirlo a mano. El separador
// correcto es el que abre bien en la máquina de quien lo recibe.
export function aCSV(fincas, { desde, hasta, negocio } = {}) {
  const L = [];
  L.push([`Compras de leche${negocio ? ' — ' + negocio : ''}`].map(campo).join(';'));
  if (desde || hasta) L.push([`Del ${desde || '…'} al ${hasta || '…'}`].map(campo).join(';'));
  L.push('');
  L.push(['Cédula/NIT', 'Proveedor', 'Finca', 'Fecha',
          'Litros vaca', 'Valor vaca', 'Litros búfala', 'Valor búfala', 'Total']
         .map(campo).join(';'));

  for (const f of fincas || []) {
    for (const d of f.dias) {
      L.push([
        f.documento || 'FALTA CÉDULA',
        f.propietario || f.finca, f.finca, d.fecha,
        d.litrosVaca || '', d.valorVaca || '',
        d.litrosBufala || '', d.valorBufala || '',
        (d.valorVaca + d.valorBufala),
      ].map(campo).join(';'));
    }
    L.push([f.documento || 'FALTA CÉDULA', f.propietario || f.finca, f.finca,
            'SUBTOTAL', f.litrosVaca, f.valorVaca, f.litrosBufala, f.valorBufala,
            f.total].map(campo).join(';'));
  }

  const t = totales(fincas);
  L.push('');
  L.push(['', '', '', 'TOTAL', t.litrosVaca, t.valorVaca,
          t.litrosBufala, t.valorBufala, t.total].map(campo).join(';'));
  return L.join('\r\n');
}

// Los números van sin separador de miles y con punto decimal para que Excel los
// lea como números y no como texto. Una columna de texto no se puede sumar, y
// lo primero que hace el contador es sumarla.
export function nombreDelArchivo(desde, hasta) {
  return `compras-leche-${desde || 'inicio'}-a-${hasta || 'hoy'}.csv`;
}

// ── La relación de leche que se le entrega a la finca ────────────────────
//
// Es la hoja que llevaban en Excel: un renglón por DÍA DEL CALENDARIO, no por
// entrega. Los días en cero salen igual, y eso no es relleno — es la prueba de
// que ese día no entregó. Sin ellos, el finquero cuenta doce renglones donde
// esperaba quince y la conversación empieza por ahí.
export function diasDelPeriodo(recibos, desde, hasta) {
  const porDia = new Map();
  for (const r of recibos || []) {
    const d = soloFecha(r.fecha);
    if (!porDia.has(d)) porDia.set(d, { fecha: d, litrosVaca: 0, valorVaca: 0,
                                        litrosBufala: 0, valorBufala: 0 });
    const a = porDia.get(d);
    if (r.tipo_leche === 'bufala') {
      a.litrosBufala += num(r.litros); a.valorBufala += num(r.total);
    } else {
      a.litrosVaca += num(r.litros); a.valorVaca += num(r.total);
    }
    // El precio del litro de ESE día, que en la factura va en su propia
    // columna. Se saca dividiendo y no se copia del recibo porque un mismo día
    // puede traer dos entregas a precios distintos: lo que va en el papel es
    // lo que salió a pagar por litro ese día, no el precio de una de las dos.
  }

  for (const a of porDia.values()) {
    a.precioVaca = a.litrosVaca > 0 ? Math.round(a.valorVaca / a.litrosVaca) : 0;
    a.precioBufala = a.litrosBufala > 0 ? Math.round(a.valorBufala / a.litrosBufala) : 0;
  }

  // Sin fechas de corte no se inventa un calendario: se devuelven los días que
  // de verdad tienen algo.
  if (!desde || !hasta) {
    return [...porDia.values()].sort((a, b) => a.fecha.localeCompare(b.fecha));
  }

  const filas = [];
  // Se recorre en UTC. Sumando días sobre una fecha local, el cambio de hora
  // repite o salta un día y la relación queda con un renglón de más o de menos.
  const d = new Date(desde + 'T00:00:00Z');
  const fin = new Date(hasta + 'T00:00:00Z');
  // Un tope por si llegan fechas absurdas: un periodo de diez años no es una
  // relación, es una pantalla colgada.
  for (let i = 0; d <= fin && i < 400; i++) {
    const clave = d.toISOString().slice(0, 10);
    filas.push(porDia.get(clave) ||
      { fecha: clave, litrosVaca: 0, valorVaca: 0, litrosBufala: 0, valorBufala: 0,
        precioVaca: 0, precioBufala: 0 });
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return filas;
}

// El pie de la relación: lo entregado, lo que se le descuenta y lo que se le
// paga. Es la cuenta que el finquero revisa con el dedo.
export function cuentaDeLaRelacion(dias, prestamos, abonos = [], periodo = null) {
  const t = (dias || []).reduce((a, d) => ({
    litrosVaca: a.litrosVaca + num(d.litrosVaca), valorVaca: a.valorVaca + num(d.valorVaca),
    litrosBufala: a.litrosBufala + num(d.litrosBufala),
    valorBufala: a.valorBufala + num(d.valorBufala),
  }), { litrosVaca: 0, valorVaca: 0, litrosBufala: 0, valorBufala: 0 });

  const total = t.valorVaca + t.valorBufala;

  // LO QUE QUEDA DEBIENDO DEL PRÉSTAMO, no lo que se le prestó.
  //
  // Descontaba `monto` —el préstamo entero— aunque el finquero llevara medio
  // pagado. Con un préstamo de $500.000 y $400.000 ya abonados, la hoja le
  // descontaba los $500.000 otra vez: cobrada dos veces la misma plata, y en el
  // papel que él revisa antes de firmar.
  //
  // Y decía otra cosa que la pantalla de pagar, que sí mira los abonos. Dos
  // cifras distintas para el mismo descuento es la peor clase de error: el que
  // se descubre con el finquero delante.
  // ── LA HOJA DE UNA SEMANA YA PAGADA NO PUEDE HABLAR DE HOY ───────────────
  //
  // A NIÑO BERMUDEZ le salieron dos relaciones de semanas distintas con los
  // MISMOS dos abonos y el MISMO saldo. Y en la del 29 de agosto al 4 de
  // septiembre aparecía un abono del 11 de septiembre — de una semana después
  // de la que dice el papel.
  //
  // Es que la hoja miraba el préstamo tal como está HOY: todos sus abonos y su
  // saldo de hoy. Para la semana que corre eso es lo correcto; para una semana
  // que ya se liquidó, no. Ese papel tiene que decir lo que se le descontó ESE
  // día y con cuánto quedó ENTONCES — es el que el finquero guarda y con el que
  // vuelve a reclamar tres meses después.
  //
  // Un día de más se lee como plata cobrada dos veces.
  const hastaEl = periodo && periodo.hasta ? String(periodo.hasta).slice(0, 10) : null;
  const desdeEl = periodo && periodo.desde ? String(periodo.desde).slice(0, 10) : null;
  const dia = (a) => String(a && a.fecha || '').slice(0, 10);

  const conSaldo = (prestamos || []).map(p => {
    const monto = Math.max(0, num(p.monto));
    const todos = (Array.isArray(abonos) ? abonos : [])
      .filter(a => String(a.prestamo_id) === String(p.id))
      .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));

    // Los de ANTES de este periodo: con eso llegaba el préstamo a esta semana.
    const antes = hastaEl ? todos.filter(a => dia(a) < desdeEl) : [];
    // Los de DENTRO: lo que se le descontó en esta liquidación.
    const dentro = hastaEl
      ? todos.filter(a => dia(a) >= desdeEl && dia(a) <= hastaEl)
      : todos;
    // Y los de después no salen. Sin periodo —una hoja sin fechas— se
    // comporta como antes y los cuenta todos.
    const suma = (l) => l.reduce((s, a) => s + Math.max(0, num(a.monto)), 0);
    const traia = Math.max(0, Math.round(monto - suma(antes)));
    const enEste = Math.round(suma(dentro));
    const abonado = Math.round(suma(antes) + suma(dentro));

    // SIN PERIODO se comporta como antes: descuenta lo que queda hoy.
    //
    // Es el camino de las pantallas que no mandan fechas. Aplicándoles la
    // regla de «lo que se descontó en este periodo», un préstamo con abonos
    // volvería a descontar los abonos enteros — que es exactamente el cobro
    // doble que se arregló ayer.
    const pendiente = Math.max(0, Math.round(monto - abonado));

    // ── LA CUOTA PACTADA ───────────────────────────────────────────────────
    //
    // Un préstamo puede llevar cuota: «$120.000, y le descuento $40.000 cada
    // quincena». La pantalla de pagar la respeta —`proponerDescuentos` la
    // mira— pero esta hoja no: prometía descontar el saldo ENTERO.
    //
    // Es otra vez el error de las dos cifras, y del peor tipo: el papel que el
    // finquero firma diría que le quitan $120.000 y el pago le quitaría
    // $40.000. Quien lo descubre es él, con la hoja en la mano.
    //
    // La cuota solo manda mientras el periodo NO se haya liquidado. Una vez
    // liquidado vale lo que se descontó de verdad, cuota o no: pudo haberse
    // cambiado a mano al pagar, y el papel tiene que decir lo que pasó.
    const cuota = num(p.cuota) > 0 ? Math.round(num(p.cuota)) : null;
    const conCuota = (s) => (cuota ? Math.min(cuota, s) : s);

    // LO QUE SE DESCUENTA EN ESTA HOJA. Si el periodo ya se liquidó, es lo que
    // se le descontó de verdad; si todavía no, es lo que se le va a descontar
    // al pagarle: la cuota, o el saldo entero cuando no hay cuota.
    // ── SIN CUOTA, LA HOJA NO DESCUENTA NADA ───────────────────────────────
    //
    // A TORIBIO ANDRADE, con un préstamo de $3.000.000 del 15 de febrero sin
    // cuota, la relación le restaba los tres millones enteros del TOTAL A
    // PAGAR. Nadie lo había autorizado: el préstamo estaba ahí y el programa
    // decidía solo.
    //
    // Y llevaba siete meses haciéndolo, porque nunca se le descontó de verdad
    // —Gabriel decía que no cada quincena—. El papel prometía una cosa y la
    // caja hacía otra.
    //
    // La cuota es la única forma de decir «descuéntele tanto cada vez». Sin
    // ella el préstamo se nombra, para que el finquero sepa que está ahí, pero
    // no se resta.
    //
    // Un periodo YA LIQUIDADO es otra cosa: manda lo que se descontó de verdad,
    // hubiera cuota o no. Ese papel cuenta lo que pasó, no lo que se pactó.
    const seDescuenta = hastaEl
      ? (enEste > 0 ? enEste : (cuota ? conCuota(traia) : 0))
      : (cuota ? conCuota(pendiente) : 0);

    // Lo que se debía ANTES de esta hoja. Sin periodo no hay «antes» que valga:
    // es lo que queda hoy. Decía `monto`, y entonces la resta de abajo daba lo
    // ya abonado en vez de lo que queda por pagar.
    const debia = hastaEl ? traia : pendiente;

    return {
      ...p, monto, abonado, abonos: dentro, cuota,
      // Con cuánto llegaba el préstamo a esta semana.
      traia: hastaEl ? traia : monto,
      saldo: seDescuenta,
      // Y CON CUÁNTO QUEDA DESPUÉS DE ESTA HOJA.
      //
      // Se resta lo que la hoja descuenta, NO los abonos ya registrados. La
      // relación de PABLO POLO decía «Menos préstamo −$120.000» y tres
      // renglones más abajo «Del préstamo le quedan $120.000»: la misma hoja
      // afirmando las dos cosas.
      //
      // Pasaba en el caso más común de todos —la quincena que todavía NO se ha
      // liquidado—. Ahí no hay abono registrado, así que `enEste` es cero y la
      // resta no descontaba nada, mientras el renglón de arriba sí anunciaba
      // los $120.000. El finquero lee que le cobran el préstamo y que además
      // sigue debiéndolo entero.
      queda: Math.max(0, debia - seDescuenta),
      liquidado: !!hastaEl && enEste > 0,
    };
  });
  const descuento = conSaldo.reduce((s, p) => s + p.saldo, 0);

  // ── LO QUE VIENE DE SEMANAS ANTERIORES ───────────────────────────────────
  //
  // «Los abonos no deben cambiar por el alza o baja, para que el cliente no
  // piense que se le está robando.»
  //
  // Cuando a una semana ya pagada se le sube el precio, esa semana queda
  // debiéndole plata al finquero y se le abona en el corte siguiente. Entonces
  // el abono de la semana siguiente sale MAYOR que su leche —$731.100 contra
  // $716.000— y el papel no decía de dónde salía la diferencia. Leído por
  // quien cobra, eso es plata que apareció de la nada.
  //
  // Y además la cuenta salía mal: con el abono por encima de la leche, la hoja
  // le decía «queda debiendo $15.100» a quien acababa de COBRAR esos $15.100.
  //
  // No se toca ningún abono. Se parte la cifra en dos y se dice cuál es cuál.
  const liquidado = conSaldo.filter(p => p.liquidado)
                            .reduce((s, p) => s + p.saldo, 0);
  const deAntes = Math.max(0, Math.round(liquidado - total));
  // Lo que de verdad se descontó contra la leche DE ESTA semana.
  const deEsta = descuento - deAntes;

  return {
    ...t, total, descuento, prestamos: conSaldo,
    // Cuánto del abono venía de semanas anteriores, para poder decirlo.
    deAntes, deEsta,
    // Nunca en negativo: si el préstamo pesa más que la leche, se descuenta
    // hasta donde alcanza y el resto queda debiendo. Es lo mismo que hace el
    // servidor al liquidar, y las dos cuentas tienen que decir lo mismo.
    aPagar: Math.max(0, total - deEsta),
    quedaDebiendo: Math.max(0, deEsta - total),
  };
}

// ── LOS CAMBIOS DE PRECIO QUE TOCARON ESTA RELACIÓN ────────────────────────
//
// «Dejarle en la factura el detalle claro, que el dueño de la finca sepa por
// qué la variación de precios, para que estén tranquilos.»
//
// Sin esto, a NIÑO BERMÚDEZ le llegaron dos relaciones donde los abonos no
// cuadraban con la leche de cada semana, y nadie —ni él, ni Gabriel, ni yo—
// podía explicar de dónde salían $15.100. Estaban en un cambio de precio hecho
// DESPUÉS de pagarle esa semana: la leche pasó a valer más y la diferencia se
// le abonó en el pago siguiente.
//
// Todo estaba bien y aun así pareció un error, porque el papel no lo decía. El
// papel que no explica una diferencia la convierte en una discusión.
//
// Se queda con los cambios que PISAN este periodo y que son de esta finca —o de
// todas, que es cuando `finca_id` va en nulo—.
export function cambiosDeEstaRelacion(cambios, finca, periodo) {
  if (!periodo || !periodo.desde) return [];
  const dia = (f) => String(f || '').slice(0, 10);
  const desde = dia(periodo.desde), hasta = dia(periodo.hasta);
  const idFinca = finca && finca.id;

  return (Array.isArray(cambios) ? cambios : [])
    .filter(c => {
      // De esta finca, o de un cambio que fue para todas.
      if (c.finca_id != null && String(c.finca_id) !== String(idFinca)) return false;
      // Y que se pise con el periodo. Dos rangos se pisan cuando ninguno
      // termina antes de que el otro empiece — se lee raro y es la única forma
      // que no se equivoca en los bordes.
      return !(dia(c.hasta) < desde || dia(c.desde) > hasta);
    })
    .map(c => ({
      ...c,
      diferencia: Math.round(num(c.total_despues) - num(c.total_antes)),
    }))
    // Solo los que MOVIERON plata. Reliquidar al mismo precio deja una fila con
    // diferencia cero, y anunciarla sería explicar algo que no pasó.
    .filter(c => c.diferencia !== 0)
    .sort((a, b) => new Date(a.cuando || 0) - new Date(b.cuando || 0));
}
